import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/data/products";
import { EASE } from "@/components/Reveal";

export const QtyControl = ({ line, small }) => {
  const { setQty } = useCart();
  const id = `${line.slug}-${line.finish}`;
  return (
    <div className={`inline-flex items-center rounded-full border border-ink/20 ${small ? "h-8" : "h-11"}`}>
      <button data-testid={`qty-minus-${id}`} onClick={() => setQty(line.slug, line.finish, line.qty - 1)} className="px-2.5 hover:text-clay" aria-label="Decrease">
        <Minus className="h-3.5 w-3.5" />
      </button>
      <span data-testid={`qty-value-${id}`} className="w-6 text-center text-sm tabular-nums">{line.qty}</span>
      <button data-testid={`qty-plus-${id}`} onClick={() => setQty(line.slug, line.finish, line.qty + 1)} className="px-2.5 hover:text-clay" aria-label="Increase">
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

const Line = ({ line }) => {
  const { setQty } = useCart();
  return (
    <li className="flex gap-4 border-b border-ink/10 py-5" data-testid={`cart-line-${line.slug}-${line.finish}`}>
      <img src={line.finishObj.image} alt={line.product.name} className="h-28 w-24 shrink-0 rounded-sm object-cover" />
      <div className="flex flex-1 flex-col">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-display text-2xl leading-none">{line.product.name}</p>
            <p className="mt-1 text-xs uppercase tracking-[0.14em] text-muted">{line.product.type}</p>
            <p className="mt-1 text-xs text-muted">{line.finishObj.name}</p>
          </div>
          <button data-testid={`cart-remove-${line.slug}-${line.finish}`} onClick={() => setQty(line.slug, line.finish, 0)} aria-label="Remove" className="text-muted hover:text-ink">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-auto flex items-center justify-between pt-3">
          <QtyControl line={line} small />
          <span className="text-sm tabular-nums">{formatINR(line.lineTotal)}</span>
        </div>
      </div>
    </li>
  );
};

export const CartDrawer = () => {
  const { open, setOpen, lines, total, count } = useCart();
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="cart-overlay" />
          <motion.aside
            data-testid="cart-drawer"
            data-lenis-prevent
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-bone text-ink"
          >
            <div className="flex items-center justify-between border-b border-ink/10 px-6 py-5">
              <p className="font-display text-3xl">Your Bag <span className="text-muted">({count})</span></p>
              <button data-testid="cart-close-button" onClick={() => setOpen(false)} aria-label="Close"><X className="h-5 w-5" /></button>
            </div>
            {lines.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-5 px-8 text-center">
                <p className="font-display text-4xl italic">Nothing here, yet.</p>
                <p className="text-sm text-muted">Pieces you add will rest here until you're ready.</p>
                <button data-testid="cart-empty-browse" onClick={() => setOpen(false)} className="btn-primary">Browse the collection</button>
              </div>
            ) : (
              <>
                <ul className="flex-1 overflow-y-auto px-6">{lines.map((l) => <Line key={`${l.slug}-${l.finish}`} line={l} />)}</ul>
                <div className="border-t border-ink/10 px-6 py-6">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs uppercase tracking-[0.18em] text-muted">Total</span>
                    <span data-testid="cart-total" className="font-display text-4xl tabular-nums">{formatINR(total)}</span>
                  </div>
                  <p className="mt-2 text-xs text-muted">Delivery & installation are arranged personally after you order.</p>
                  <Link to="/checkout" data-testid="cart-checkout-button" onClick={() => setOpen(false)} className="btn-primary mt-5 w-full">
                    Proceed to checkout
                  </Link>
                </div>
              </>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
